/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderUserContext } from '@gh/builder-shared';
import { LeadBoardListItemParams, Priority, Stage } from '@gh/builder-shared-data';
import { EnumDictionary } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { NotificationService, WaitingService } from '@gh/core-ui';

import { Observable } from 'rxjs';

import { LeadBoardBusinessService } from '../../resources/lead-board-business.service';
import {
  cardUpdateParams,
  isLeadBoardItemHasSameColumnId,
  LeadBoardColumnId,
  LeadBoardListItem,
  LeadBoardMobileFilter,
  LeadBoardScroll,
  LeadBoardScrollTarget,
  SalespersonSelectItem,
} from '../../resources/lead-board.model';
import { LeadBoardFormState } from './lead-board-form.service';
import { VirtualPosition } from './virtual-rendering';
import { isFunction } from 'lodash';

@Injectable()
export class LeadBoardCommandService {

  constructor(private route: ActivatedRoute,
              private businessService: LeadBoardBusinessService,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private userContext: BuilderUserContext,
              private form: LeadBoardFormState) {
  }

  filterLeadBoardCardsByInfo(callback?: any): void {
    const { salesPersonId, showCompleted, showActive, showInactive, showOverdue, showSuppressed, searchString } = this.form.value.info;
    this.businessService.findAll(salesPersonId, { showCompleted, showActive, showInactive, showOverdue, showSuppressed, searchString }).pipe(
      this.waitingService.operator())
      .subscribe((list: LeadBoardListItem[]) => {
        this.form.refreshBoardList(list);
        this.form.resetScroll();
        if (callback && isFunction(callback)) {
          callback();
        }
      });
  }

  buildForm(salesPersonId: Maybe<ID>,
            salespersonList: SalespersonSelectItem[],
            list: LeadBoardListItem[],
            priorities: EnumDictionary<Priority>,
            stages: EnumDictionary<Stage>,
            isEditMode: boolean,
            leadBordFilter: any): void {
    this.form.build(salesPersonId, salespersonList, list, priorities, stages, isEditMode, leadBordFilter);
  }

  setSalespersonId(id: Maybe<ID>): void {
    this.form.setSalespersonId(id);
  }

  setSearchString(value: Maybe<string>): void {
    this.form.setSearchString(value);
  }

  setInfoShowCompletedFilter(isVisible: boolean): void {
    this.form.setInfoShowCompletedFilter(isVisible);
  }

  setInfoShowActiveFilter(isVisible: boolean): void {
    this.form.setInfoShowActiveFilter(isVisible);
  }

  setInfoShowInactiveFilter(isVisible: boolean): void {
    this.form.setInfoShowInactiveFilter(isVisible);
  }

  setInfoShowOverdueFilter(isVisible: boolean): void {
    this.form.setInfoShowOverdueFilter(isVisible);
  }

  startDrag(card: LeadBoardListItem): void {
    this.form.setItemAsDragged(card, true);
  }

  stopDrag(card: LeadBoardListItem): void {
    this.form.setItemAsDragged(card, false);
  }

  enterZone(columnId: LeadBoardColumnId, card: LeadBoardListItem): void {
    this.form.enterZone(columnId, card);
  }

  exitZone(columnId: LeadBoardColumnId, card: LeadBoardListItem): void {
    this.form.exitZone(columnId, card);
  }

  dropItem(card: LeadBoardListItem, columnId: LeadBoardColumnId): void {
    if (isLeadBoardItemHasSameColumnId(card, columnId)) {
      return;
    }

    this.updateCard(card.salesFolderId, {
      ...cardUpdateParams(card),
      ...columnId,
    }).subscribe(({ version }: LeadBoardListItemParams) => {
      this.moveCard(card, columnId);
      this.form.refreshCardVersion(card, version);
    }, this.notificationService.processAll);
  }

  updateFollowUpDate(card: LeadBoardListItem, followUpDate: Date): void {
    this.updateCard(card.salesFolderId, {
      ...cardUpdateParams(card),
      followUpDate,
    }).subscribe(({ version }: LeadBoardListItemParams) => {
      this.form.refreshCardVersion(card, version);
      this.form.setSalesFolderFollowUpDate(card, followUpDate);
    }, this.notificationService.processAll);
  }

  incrementCardActivitiesCount(card: LeadBoardListItem): void {
    this.form.incrementCardActivitiesCount(card);
  }

  moveCard(card: LeadBoardListItem, moveParams: LeadBoardColumnId): void {
    this.form.moveCard(card, moveParams);
  }

  setMobileFilter(filter: LeadBoardMobileFilter): void {
    this.form.setMobileFilter(filter);
  }

  setMobilePriority(priority: number): void {
    this.form.setMobilePriority(priority);
    this.form.resetScroll();
  }

  setMobileStage(stage: number): void {
    this.form.setMobileStage(stage);
    this.form.resetScroll();
  }

  setFilter(filter: any): void {
    this.form.setFilter(filter);
  }

  setScroll(scroll: LeadBoardScroll): void {
    this.form.setScroll(scroll);
  }

  setScrollPosition(position: VirtualPosition): void {
    this.form.setScroll({ target: LeadBoardScrollTarget.Standard, position });
  }

  setMobileScrollPosition(position: VirtualPosition): void {
    this.form.setScroll({ target: LeadBoardScrollTarget.Mobile, position });
  }

  toggleCard(id: ID): void {
    this.form.toggleCard(id);
  }

  setStartFilter(filter: any): void {
    this.form.setStartFilter(filter);
  }

  private updateCard(salesFolderId: ID, params: LeadBoardListItemParams): Observable<LeadBoardListItemParams> {
    return this.businessService.update(salesFolderId, params).pipe(
      this.waitingService.operator());
  }
}
